import type { DiscordActivityStuckJob } from '@runhuman/shared';
import { openAdminJobDetail } from '../tester/harness';
import { ExternalLinkIcon } from '../../ExternalLinkIcon';

interface Props {
  readonly stuckJobs: readonly DiscordActivityStuckJob[];
  readonly busyJobId: string | null;
  readonly onRelease: (jobId: string) => void;
  readonly onTerminate: (jobId: string) => void;
}

/**
 * Alert strip listing every stuck job (working past `responseDeadline + grace`)
 * with Release and Terminate buttons per row. Hidden entirely when there are
 * no stuck jobs — the admin shouldn't see a "0 alerts" placeholder, the
 * absence of the strip IS the all-clear signal.
 *
 * `busyJobId` disables both buttons on whichever row is currently mid-mutation
 * to prevent double-clicks. The next ops-snapshot poll (or the explicit
 * invalidation triggered on success) refreshes the list.
 */
export function StuckJobsAlertStrip({ stuckJobs, busyJobId, onRelease, onTerminate }: Props) {
  if (stuckJobs.length === 0) return null;

  return (
    <section className="admin-stuck-jobs" role="alert">
      <h2>Stuck jobs ({stuckJobs.length})</h2>
      <ul className="admin-stuck-jobs__list">
        {stuckJobs.map((job) => {
          const isBusy = busyJobId === job.id;
          return (
            <li key={job.id} className="admin-stuck-jobs__row">
              <div className="admin-stuck-jobs__meta">
                <span className="admin-stuck-jobs__tester">
                  {job.assignedTesterName ?? 'Unassigned'}
                </span>
                <span className="admin-stuck-jobs__lateness">
                  {job.minutesPastDeadline} min past deadline
                </span>
              </div>
              <div className="admin-stuck-jobs__id">{job.id}</div>
              <div className="admin-stuck-jobs__actions">
                <button
                  type="button"
                  className="admin-stuck-jobs__view"
                  onClick={() => void openAdminJobDetail(job.id)}
                >
                  View
                  <ExternalLinkIcon />
                </button>
                <button
                  type="button"
                  className="admin-stuck-jobs__release"
                  onClick={() => onRelease(job.id)}
                  disabled={isBusy}
                >
                  Release
                </button>
                <button
                  type="button"
                  className="admin-stuck-jobs__terminate"
                  onClick={() => onTerminate(job.id)}
                  disabled={isBusy}
                >
                  Terminate
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
